import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import meetingService from '../../services/meetingService';
import projectService from '../../services/projectService';

const MeetingEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meeting_type: 'Other',
    start_time: '',
    end_time: '',
    location: '',
    meeting_url: '',
    project_id: '',
    agenda: '',
    minutes: '',
    status: 'Scheduled',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchMeeting();
    fetchProjects();
  }, [id]);

  const fetchMeeting = async () => {
    try {
      setLoading(true);
      const data = await meetingService.getMeeting(id);
      const meeting = data.data;
      
      // Format datetime for input fields
      const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().slice(0, 16);
      };

      setFormData({
        title: meeting.title || '',
        description: meeting.description || '',
        meeting_type: meeting.meeting_type || 'Other',
        start_time: formatDateTime(meeting.start_time),
        end_time: formatDateTime(meeting.end_time),
        location: meeting.location || '',
        meeting_url: meeting.meeting_url || '',
        project_id: meeting.project_id || '',
        agenda: meeting.agenda || '',
        minutes: meeting.minutes || '',
        status: meeting.status || 'Scheduled',
      });
    } catch (error) {
      console.error('Error fetching meeting:', error);
      toast.error('Failed to fetch meeting details');
      navigate('/app/meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 1000 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.meeting_type) {
      newErrors.meeting_type = 'Meeting type is required';
    }

    if (!formData.start_time) {
      newErrors.start_time = 'Start time is required';
    }

    if (!formData.end_time) {
      newErrors.end_time = 'End time is required';
    }

    if (formData.start_time && formData.end_time) {
      const start = new Date(formData.start_time);
      const end = new Date(formData.end_time);
      if (end <= start) {
        newErrors.end_time = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setSaving(true);

      const meetingData = {
        ...formData,
        project_id: formData.project_id || null,
      };

      await meetingService.updateMeeting(id, meetingData);
      toast.success('Meeting updated successfully');
      navigate(`/app/meetings/${id}`);
    } catch (error) {
      console.error('Error updating meeting:', error);
      toast.error(error.response?.data?.error || 'Failed to update meeting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading meeting details...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Meeting</h1>
        <p className="text-gray-600 mt-1">Update meeting details</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2`}
              placeholder="e.g., Sprint Planning Meeting"
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              placeholder="Meeting description..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meeting Type <span className="text-red-500">*</span>
              </label>
              <select
                name="meeting_type"
                value={formData.meeting_type}
                onChange={handleChange}
                className={`w-full border ${errors.meeting_type ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2`}
              >
                <option value="Standup">Standup</option>
                <option value="Planning">Planning</option>
                <option value="Review">Review</option>
                <option value="Retrospective">Retrospective</option>
                <option value="Other">Other</option>
              </select>
              {errors.meeting_type && <p className="text-red-500 text-sm mt-1">{errors.meeting_type}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
              >
                <option value="Scheduled">Scheduled</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project (Optional)</label>
            <select
              name="project_id"
              value={formData.project_id}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">No Project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Schedule */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Schedule</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                className={`w-full border ${errors.start_time ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2`}
              />
              {errors.start_time && <p className="text-red-500 text-sm mt-1">{errors.start_time}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                className={`w-full border ${errors.end_time ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2`}
              />
              {errors.end_time && <p className="text-red-500 text-sm mt-1">{errors.end_time}</p>}
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Location</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Physical Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              placeholder="e.g., Conference Room A"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Virtual Meeting URL</label>
            <input
              type="url"
              name="meeting_url"
              value={formData.meeting_url}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              placeholder="e.g., https://zoom.us/j/123456789"
            />
          </div>
        </div>

        {/* Agenda */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Agenda</h2>

          <div>
            <textarea
              name="agenda"
              value={formData.agenda}
              onChange={handleChange}
              rows="4"
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              placeholder="Meeting agenda items..."
            />
          </div>
        </div>

        {/* Minutes */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Meeting Minutes</h2>

          <div>
            <textarea
              name="minutes"
              value={formData.minutes}
              onChange={handleChange}
              rows="4"
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              placeholder="Meeting minutes and notes..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate(`/app/meetings/${id}`)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <FiX /> Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <FiSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MeetingEdit;


